// TableManagementSection
// Renders three related cards:
//   1. "Activate + Assign Table" form
//   2. "Column-Level Fields" card
//   3. "Configured Tables" table
// All state and handlers are owned by ConsoleAdmin.tsx and passed in as props.

import React, { FormEvent } from 'react';
import { ConsoleManagedTable, ConsoleTableCatalogItem, ConsoleUser } from '../../../types';
import { ColumnFieldRow } from './types';

interface TableManagementSectionProps {
  // Assign Table form state
  selectedTableId: string;
  setSelectedTableId: (v: string) => void;
  dailyRefresh: boolean;
  setDailyRefresh: (v: boolean) => void;
  primaryDeveloper: string;
  setPrimaryDeveloper: (v: string) => void;
  secondaryDeveloper: string;
  setSecondaryDeveloper: (v: string) => void;
  tableCatalog: ConsoleTableCatalogItem[];
  activeAssignableUsers: ConsoleUser[];
  handleAssignTable: (event: FormEvent) => Promise<void>;

  // Column Fields state
  columnFieldTableId: string;
  setColumnFieldTableId: (v: string) => void;
  columnFields: ColumnFieldRow[];
  setColumnFields: React.Dispatch<React.SetStateAction<ColumnFieldRow[]>>;
  updateColumnFieldRow: (index: number, key: keyof ColumnFieldRow, value: string) => void;
  removeColumnFieldRow: (index: number) => void;
  handleSaveColumnFields: () => Promise<void>;

  // Configured Tables table
  assignedTables: ConsoleManagedTable[];
  loadingTables: boolean;
  tableDeleteLoadingId: number | null;
  handleToggleTableSoftDelete: (row: ConsoleManagedTable) => Promise<void>;
}

const TableManagementSection: React.FC<TableManagementSectionProps> = ({
  selectedTableId,
  setSelectedTableId,
  dailyRefresh,
  setDailyRefresh,
  primaryDeveloper,
  setPrimaryDeveloper,
  secondaryDeveloper,
  setSecondaryDeveloper,
  tableCatalog,
  activeAssignableUsers,
  handleAssignTable,
  columnFieldTableId,
  setColumnFieldTableId,
  columnFields,
  setColumnFields,
  updateColumnFieldRow,
  removeColumnFieldRow,
  handleSaveColumnFields,
  assignedTables,
  loadingTables,
  tableDeleteLoadingId,
  handleToggleTableSoftDelete,
}) => {
  return (
    <>
      {/* ── Assign Table Form ── */}
      <div className="console-grid two-col">
        <div className="console-card">
          <h2>Activate + Assign Table</h2>
          <form className="console-form" onSubmit={handleAssignTable}>
            <label>
              Table (from `dbo.table_metadata_triggers`)
              <select value={selectedTableId} onChange={(e) => setSelectedTableId(e.target.value)} required>
                <option value="">Select table</option>
                {tableCatalog.map((item) => (
                  <option key={item.id} value={item.id}>
                    #{item.id} {item.snowflakes_table_name} ({item.postgres_table_name})
                  </option>
                ))}
              </select>
            </label>
            <label className="checkbox-row">
              <input type="checkbox" checked={dailyRefresh} onChange={(e) => setDailyRefresh(e.target.checked)} />
              Active (daily_refresh)
            </label>
            <label>
              Primary User
              <select value={primaryDeveloper} onChange={(e) => setPrimaryDeveloper(e.target.value)} required>
                <option value="">Select user</option>
                {activeAssignableUsers.map((entry) => (
                  <option key={`primary-${entry.user_email}`} value={entry.user_email}>
                    {entry.user_email}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Secondary User
              <select value={secondaryDeveloper} onChange={(e) => setSecondaryDeveloper(e.target.value)} required>
                <option value="">Select user</option>
                {activeAssignableUsers.map((entry) => (
                  <option key={`secondary-${entry.user_email}`} value={entry.user_email}>
                    {entry.user_email}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit">Save Table Assignment</button>
          </form>
        </div>

        {/* ── Notes Card (originally paired in two-col grid with Configured Tables) ── */}
        <div className="console-card">
          <h2>Notes</h2>
          <p>Admin role controls user management and assignments only.</p>
          <p>
            Table profiling and dashboard content authoring happen in `Tables` and `Dashboard` sections by
            assigned developers.
          </p>
          <p>Soft delete is non-destructive and can be restored from this screen.</p>
        </div>
      </div>

      {/* ── Column-Level Fields Card ── */}
      <div className="console-card">
        <h2>Column-Level Fields</h2>
        <p>Standalone admin system for per-table column field schema.</p>

        <div className="console-form">
          <label>
            Table
            <select
              value={columnFieldTableId}
              onChange={(e) => setColumnFieldTableId(e.target.value)}
            >
              <option value="">Select table</option>
              {assignedTables.map((row) => (
                <option key={`column-field-table-${row.table_id}`} value={row.table_id}>
                  #{row.table_id} {row.snowflakes_table_name} ({row.postgres_table_name})
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="console-subcard">
          <div className="inline-header">
            <h3>Field Schema</h3>
            <button
              type="button"
              onClick={() => setColumnFields((prev) => [...prev, { field_key: '', field_label: '' }])}
            >
              Add Field
            </button>
          </div>
          <p>Define reusable fields for every column in the selected table.</p>
          {columnFields.map((row, index) => (
            <div key={`field-row-${index}`} className="alias-row">
              <input
                value={row.field_key}
                onChange={(e) => updateColumnFieldRow(index, 'field_key', e.target.value)}
                placeholder="field_key (example: alias)"
              />
              <input
                value={row.field_label}
                onChange={(e) => updateColumnFieldRow(index, 'field_label', e.target.value)}
                placeholder="field_label (example: Alias)"
              />
              <button type="button" onClick={() => removeColumnFieldRow(index)}>
                Delete
              </button>
            </div>
          ))}
          <button type="button" onClick={() => void handleSaveColumnFields()} disabled={!columnFieldTableId}>
            Save Field Schema
          </button>
        </div>
      </div>

      {/* ── Configured Tables Table ── */}
      <div className="console-grid two-col">
        <div className="console-card">
          <h2>Configured Tables</h2>
          {loadingTables ? <p>Loading table metadata...</p> : null}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Table ID</th>
                  <th>Table</th>
                  <th>Active</th>
                  <th>Primary</th>
                  <th>Secondary</th>
                  <th>Approval</th>
                  <th>Deleted</th>
                  <th>Column Fields</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {assignedTables.map((row) => (
                  <tr key={row.table_id}>
                    <td>{row.table_id}</td>
                    <td>{row.snowflakes_table_name}</td>
                    <td>{row.daily_refresh ? 'Yes' : 'No'}</td>
                    <td>{row.primary_developer_email || '-'}</td>
                    <td>{row.secondary_developer_email || '-'}</td>
                    <td>{row.approval_status || 'draft'}</td>
                    <td>{row.is_deleted ? 'Yes' : 'No'}</td>
                    <td>{row.column_field_schema?.length || 0}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => void handleToggleTableSoftDelete(row)}
                        disabled={tableDeleteLoadingId === row.table_id}
                      >
                        {tableDeleteLoadingId === row.table_id
                          ? 'Saving...'
                          : row.is_deleted
                            ? 'Restore'
                            : 'Soft Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
                {!assignedTables.length && !loadingTables ? (
                  <tr>
                    <td colSpan={9}>No table assignments yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default TableManagementSection;